<script setup lang="ts">
import {
  useCategoryFiltersQuery,
  useCategoryProductsFastQuery,
  useNavQuery,
} from "~~/app/composables/queries/useStoreApi";
import type { NavItem } from "~~/shared/types/store";
import { useAmazzingFilterPanels } from "~~/app/composables/queries/useAmazzingFilterPanels";
import { polishProductPlural } from "~~/app/composables/useProductPlural";
import { mergeStableCategoryTree } from "~~/app/utils/categoryStableFilterTree";
import { hasSemanticListingFilter } from "~~/shared/utils/listingQueryFilters";
import { buildPrimaryListingQuery } from "~~/shared/utils/listingQuery";
import { homeContentDefaultForBrand } from "~~/shared/constants/homeContentDefault";
import { reconcileListingTotal } from "~~/shared/utils/listingPagination";
import { useAnalytics } from "~~/app/composables/useAnalytics";

const route = useRoute();
const analytics = useAnalytics();
const STABLE_CATEGORY_TREE_ROOT_ID = "2";
const STABLE_CATEGORY_TREE_CURRENT_URL = "/2-strona-glowna";

/**
 * Handles old PrestaShop URL formats:
 *   /{id}-{slug}                        e.g. /16190-kobieta
 *   /{parent-slug}/{id}-{slug}          e.g. /odziez-damska/16226-sukienki-damskie
 *
 * The numeric category ID is always the prefix of the LAST path segment.
 */
const segments = computed(() => {
  const raw = route.params.slug;
  return Array.isArray(raw) ? raw : [raw ?? ""];
});

const lastSegment = computed(
  () => segments.value[segments.value.length - 1] ?? "",
);

const categoryId = computed(() => {
  const match = lastSegment.value.match(/^(\d+)-/);
  return match?.[1] ? parseInt(match[1], 10) : null;
});

if (!categoryId.value) {
  throw createError({ statusCode: 404, statusMessage: "Strona nie istnieje" });
}

const { getCategoryBanner } = useBanners();
const categoryBanner = computed(() => getCategoryBanner(lastSegment.value));

// Key order is load-bearing: it feeds the Nitro SWR cache key (hash of raw
// req.url). buildPrimaryListingQuery is the shared source of truth the cache
// warmer mirrors — see shared/utils/listingQuery.ts.
const primaryListingQuery = computed(() => ({
  ...route.query,
  ...buildPrimaryListingQuery(route.fullPath),
}));
const hasActiveSemanticFilters = computed(() =>
  hasSemanticListingFilter(route.query),
);
const productsQuery = useCategoryProductsFastQuery(
  computed(() => String(categoryId.value ?? 0)),
  primaryListingQuery,
);
const {
  data: productsData,
  isLoading: productsLoading,
  isFetching: productsFetching,
} = productsQuery;

// Nagłówek cache HTML kategorii — pobrany w setupie (przed await), ustawiany niżej.
const cacheControlHeader = useResponseHeader("cache-control");

// SSR guard: jeśli backend nie zna kategorii (404 / brak danych), rzucamy
// 404 zamiast renderować pustą stronę kategorii.
if (import.meta.server) {
  await productsQuery.suspense().catch(() => {});
  if (!productsData.value?.category) {
    throw createError({ statusCode: 404, statusMessage: "Strona nie istnieje" });
  }
  // Cache HTML kategorii na CDN (s-maxage). Strony /{id}-slug nie miały ŻADNEGO
  // cache-control → każde wejście = pełny render SSR (sufit przepustowości pod ruchem;
  // routeRule "/category/**" nie łapie tych URL-i — analogicznie do /nowosci, /marki).
  // Cache'ujemy TYLKO gdy lista NIE jest pusta: pusta siatka to okno reindeksu
  // ElasticJetSearch ("1–0 z N"), którego nie wolno przykleić (jak guard w
  // server/api/store/categories/products/[id].get.ts).
  if ((productsData.value.products?.length ?? 0) > 0) {
    // s-maxage=600 (10 min): cache CZASOWY (nie per-zmiana). Cloudflare cache'uje
    // HTML kategorii (Cache Rule respect_origin), a webhook invalidate NIE czyści
    // CDN — purge per-sprzedaż byłby przy tym churnie (większość SKU = 1 szt.)
    // samobójczy — więc TTL to jedyny suwak świeżości. serve_stale/SWR sprawia,
    // że użytkownik NIGDY nie czeka (edge serwuje natychmiast, odświeża w tle),
    // więc TTL steruje tylko tym, jak długo wyprzedany produkt może wisieć na
    // liście: ~10 min zamiast ~2 min. Zysk: 5× rzadszy re-render origInu + wyższy
    // hit-ratio na edge (stabilniejszy TTFB, PSI częściej łapie HIT niż EXPIRED).
    cacheControlHeader.value =
      "public, s-maxage=600, stale-while-revalidate=86400";
  }
}

const {
  data: filtersData,
  isLoading: filtersLoading,
  isFetching: filtersFetching,
} = useCategoryFiltersQuery(
  computed(() => String(categoryId.value ?? 0)),
  primaryListingQuery,
  {
    ssr: false,
    enabled: computed(() => !hasActiveSemanticFilters.value),
  },
);

const stableCategoryTreeQuery = computed(() => ({
  currentUrl: STABLE_CATEGORY_TREE_CURRENT_URL,
  filters_scope: "primary",
  primary_filters_limit: 10,
  // Twardy cap liczby WARTOŚCI na grupę facetu (backend, opt-in). Drzewo stabilne
  // jest od ROOTA "2", więc każda grupa enumeruje wartości CAŁEGO sklepu (np. 1000+
  // producentów) — setki KB w SSR payloadzie, a widok wstępny i tak pokazuje 10, a
  // „więcej" ≤50. Cap=50 zachowuje toggle „więcej"/quicksearch, ucina balast. Pełną
  // listę per-kategoria daje „pokaż wszystkie filtry" (fullListingState, scope=all).
  primary_values_limit: 50,
}));

// ssr:true — MUSI zostać SSR. Cały widok filtrów jest zasilany WYŁĄCZNIE z tego
// drzewa: plugin vue-query dehydratuje tylko zapytania status==='success', a
// per-kategoryjny `filtersData` (ssr:false) NIE dociąga się na kliencie przy
// pierwszym renderze (potwierdzone na prodzie: filtry renderują się przy ZERO
// requestów /categories/filters — pochodzą z prefetchowanego+zdehydratowanego
// stable tree). Próba ssr:false (MR !61) urwała jedyne źródło danych → filtry
// znikały przy bezpośrednim wejściu / hard-refresh (cofnięte tutaj).
//
// UWAGA WYDAJNOŚĆ: od ROOTA "2" to facety CAŁEGO sklepu — na lavmag ~2 MB JSON w
// __NUXT_DATA__ (~70 % dokumentu HTML), co topiło mobile FCP/TBT. Dominującym
// balastem była enumeracja WSZYSTKICH wartości każdego facetu dla całego sklepu.
// Fix backendowy `primary_values_limit` (wyżej) tnie to o ~43 % (lokalnie stable
// 503→287 KB). Podłogą pozostaje samo drzewo kategorii `c` (nawigacja, ~246 KB
// lokalnie) — jego odchudzenie wymagałoby osobnego, ostrożnego refactoru nawigacji.
const { data: stableFiltersData } = useCategoryFiltersQuery(
  computed(() => STABLE_CATEGORY_TREE_ROOT_ID),
  stableCategoryTreeQuery,
  {
    ssr: true,
    enabled: true,
  },
);

const category = computed(
  () => productsData.value?.category ?? filtersData.value?.category,
);
const productListing = computed(() => productsData.value?.listing ?? null);
const filtersListing = computed(() => filtersData.value?.listing ?? null);
const primaryFiltersListing = computed(() =>
  hasActiveSemanticFilters.value ? productListing.value : filtersListing.value,
);
const stableFiltersListing = computed(
  () => stableFiltersData.value?.listing ?? null,
);
const primaryVisibleListing = computed(() =>
  mergeStableCategoryTree({
    currentListing: primaryFiltersListing.value,
    stableListing: stableFiltersListing.value,
    currentUrl: route.fullPath,
  }),
);
const panels = useAmazzingFilterPanels({
  currentUrl: computed(
    () => primaryVisibleListing.value?.currentUrl ?? route.fullPath,
  ),
  filtersMeta: computed(() => primaryVisibleListing.value?.filtersMeta),
});

const fullListingQuery = computed(() => ({
  ...route.query,
  currentUrl: route.fullPath,
  filters_scope: "all",
  primary_filters_limit: 10,
}));

const fullListingState = useCategoryFiltersQuery(
  computed(() => String(categoryId.value ?? 0)),
  fullListingQuery,
  {
    enabled: computed(
      () => panels.showAllFilters.value && panels.canExpandFilters.value,
    ),
  },
);

const visibleListing = computed(() => {
  if (panels.showAllFilters.value && fullListingState.data.value?.listing) {
    return mergeStableCategoryTree({
      currentListing: fullListingState.data.value.listing,
      stableListing: stableFiltersListing.value,
      currentUrl: route.fullPath,
    });
  }

  return primaryVisibleListing.value;
});
const products = computed(() => productsData.value?.products ?? []);
const pagination = computed(() => {
  const base = productsData.value?.pagination;
  // Ścieżka semantyczna: produkty i facety pochodzą z tej samej odpowiedzi amazzing
  // (productsData, response_scope=full), więc totale już się zgadzają.
  if (hasActiveSemanticFilters.value) {
    return base;
  }
  // Zwykła strona kategorii: total nagłówka z ElasticJetSearch, a licznik bieżącej
  // kategorii w drzewie filtrów z read-modelu. Wyrównujemy napis do liczby w filtrach,
  // gdy read-model jest już dostępny (klient). SSR zostaje na Elasticu (filters: ssr:false).
  return reconcileListingTotal(base, filtersListing.value?.pagination?.totalItems);
});
const sortOptions = computed(
  () =>
    productListing.value?.sorting?.options ??
    filtersListing.value?.sorting?.options ??
    [],
);
const currentSort = computed(
  () =>
    productListing.value?.sorting?.currentKey ??
    filtersListing.value?.sorting?.currentKey ??
    "",
);
// Optimistic local copy — bez tego native <select> mignie do starej wartości
// na czas refetchu (currentSort dochodzi z TanStack Query po round-tripie).
const selectedSort = ref(currentSort.value);
watch(
  currentSort,
  (v) => {
    if (v) selectedSort.value = v;
  },
  { immediate: true },
);
const primaryFiltersLoading = computed(() =>
  hasActiveSemanticFilters.value ? productsLoading.value : filtersLoading.value,
);
const primaryFiltersFetching = computed(() =>
  hasActiveSemanticFilters.value
    ? productsFetching.value
    : filtersFetching.value,
);

function onSortChange(key: string) {
  selectedSort.value = key;
  navigateTo({ query: { ...route.query, order: key, page: undefined } });
}
const canExpandFilters = computed(
  () => panels.canExpandFilters.value && !panels.showAllFilters.value,
);
const canCollapseFilters = computed(
  () =>
    panels.showAllFilters.value &&
    Boolean(fullListingState.data.value?.listing),
);
const isExpandingFilters = computed(
  () => panels.showAllFilters.value && fullListingState.isFetching.value,
);
const expandFiltersError = computed(() =>
  panels.showAllFilters.value && fullListingState.error.value
    ? "Nie udało się załadować dodatkowych filtrów."
    : null,
);

const { data: nav } = useNavQuery();

function findNavPath(
  items: NavItem[],
  categoryId: string,
  path: NavItem[] = [],
): NavItem[] | null {
  for (const item of items) {
    const lastSeg = item.href.split("/").filter(Boolean).at(-1) ?? "";
    if (lastSeg.startsWith(`${categoryId}-`)) return [...path, item];
    if (item.children.length) {
      const found = findNavPath(item.children, categoryId, [...path, item]);
      if (found) return found;
    }
  }
  return null;
}

const breadcrumbs = computed(() => {
  const base: { label: string; href?: string }[] = [
    { label: "Strona główna", href: "/" },
  ];
  const id = categoryId.value ? String(categoryId.value) : null;

  if (id && nav.value?.items) {
    const path = findNavPath(nav.value.items, id);
    if (path) {
      for (let i = 0; i < path.length - 1; i++) {
        base.push({ label: path[i]!.label, href: path[i]!.href });
      }
      base.push({ label: category.value?.name ?? path.at(-1)!.label });
      return base;
    }
  }

  // fallback: derive from URL segments
  const segs = segments.value;
  for (let i = 0; i < segs.length - 1; i++) {
    const seg = segs[i] ?? "";
    const label = seg
      .replace(/^\d+-/, "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
    base.push({ label, href: "/" + segs.slice(0, i + 1).join("/") });
  }
  base.push({
    label:
      category.value?.name ??
      lastSegment.value
        .replace(/^\d+-/, "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
  });
  return base;
});

// Zwykły boolean, nie ref — `.value` dałoby undefined i wariant katalogowy
// wszędzie. Patrz useCatalogOnly.
const { sellingEnabled } = useCatalogOnly();

/**
 * Slug DZIAŁU nadrzędnego — klucz ikony w banerze sklepu katalogowego.
 *
 * Bierzemy dział, a nie bieżącą kategorię: rysunki są narysowane dla
 * dziewięciu działów, a podkategoria („power-banki") dostałaby kształt
 * zapasowy. `findNavPath` zwraca ścieżkę od działu głównego, więc pierwszy
 * element jest tym, czego szukamy. Zanim menu się załaduje, dział wyciągamy
 * z pierwszego segmentu adresu — w obu wariantach URL-a jest nim dział
 * (`/3-elektronika`, `/elektronika/12-power-banki`).
 */
const dzialSlug = computed(() => {
  const id = categoryId.value ? String(categoryId.value) : null;
  const sciezka = id && nav.value?.items ? findNavPath(nav.value.items, id) : null;
  if (sciezka?.[0]?.slug) return sciezka[0].slug;
  return (segments.value[0] ?? "").replace(/^\d+-/, "");
});

// Tytuł na chwilę przed załadowaniem kategorii. „MODA LESS WASTE" to hasło
// lavmaga — w sklepie z gadżetami byłoby cudzym sloganem nad listingiem.
// Sklep sprzedażowy bierze hasło z treści zapasowej SWOJEJ marki (lavmag
// i youlook: „MODA LESS WASTE" jak dotąd; discanto: własne).
const tytulZapasowy = sellingEnabled
  ? homeContentDefaultForBrand(useBrand().id).hero.text
  : "KATALOG";

watch(lastSegment, () => {
  if (Object.keys(route.query).length > 0) {
    navigateTo({ query: {} });
  }
});

watchEffect(() => {
  if (!category.value) return;
  // seoTitle = meta_title z category_lang (panel PS), fallback na nazwę kategorii.
  // Sufiks brandu (· LAVMAG) dokleja globalny titleTemplate w useBrandHead.
  useStorefrontSeo({
    title: category.value.seoTitle,
    description: category.value.seoDescription,
  });
});

const _listItemListId = computed(() => `category_${categoryId.value ?? ""}`);
const _listItemListName = computed(
  () => category.value?.name ?? lastSegment.value ?? "Kategoria",
);
let _lastTrackedListKey = "";
watch(
  [products, _listItemListId],
  ([list, id]) => {
    if (!import.meta.client || !list.length) return;
    const key = `${id}|${list.length}|${list[0]?.id ?? ""}`;
    if (key === _lastTrackedListKey) return;
    _lastTrackedListKey = key;
    analytics.trackViewItemList(list, {
      itemListId: id,
      itemListName: _listItemListName.value,
    });
    if (categoryId.value) {
      analytics.trackViewCategory(categoryId.value, _listItemListName.value);
    }
  },
  { immediate: true },
);

// ── GSAP scroll animations ──
type _GsapCtx = { revert(): void; add(fn: () => void): void };
type _GsapApi = {
  registerPlugin(p: unknown): void;
  context(fn: () => void, scope?: Element | null): _GsapCtx;
  fromTo(
    targets: HTMLElement | HTMLElement[],
    from: Record<string, unknown>,
    to: Record<string, unknown>,
  ): void;
};
type _StApi = { getAll(): Array<{ kill(): void }> };

let _gsap: _GsapApi | null = null;
let _st: _StApi | null = null;
let _ctx: _GsapCtx | null = null;
let _ctxNewsletter: { revert(): void } | null = null;
let _unwatchAnim: (() => void) | null = null;
// Karty siatki animowane w bieżącym cyklu (reveal jednorazowy na kartę). WeakSet, bo
// przy zmianie kategorii/sortowania stare węzły DOM znikają i są zbierane automatycznie.
let _animatedCards = new WeakSet<HTMLElement>();
// Obserwator doładowań: karty 5+ (LazyProductCard) montują się długo po pierwszym
// _animateGrid() — patrz _observeGrid().
let _gridObserver: MutationObserver | null = null;
let _rafReveal = 0;
const introAnimationsEnabled = ref(true);

const _REVEAL = { duration: 0.7, ease: "power2.out" } as const;
const _ST_OPTS = {
  start: "top 85%",
  toggleActions: "play none none none",
} as const;

function _els(selector: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(selector));
}

function _el(selector: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(selector);
}

function _animateCatHero(): void {
  if (!_gsap) return;
  const heroEls = [..._els(".cat-hero__title"), ..._els(".cat-hero__cta")];
  if (!heroEls.length) return;
  _gsap.fromTo(
    heroEls,
    { opacity: 0, y: 20 },
    {
      ..._REVEAL,
      opacity: 1,
      y: 0,
      stagger: 0.12,
    },
  );
}

// Reveal pojedynczych kart w obrębie trwałego kontekstu (_ctx). Dokładamy
// przyrostowo — BEZ revert() tutaj, bo to zabiłoby ScrollTriggery kart już
// animowanych. Każdą kartę animujemy raz (strażnik _animatedCards).
function _revealCards(cards: HTMLElement[]): void {
  if (!_gsap || !_ctx || !cards.length) return;
  const g = _gsap;
  _ctx.add(() => {
    cards.forEach((card) => {
      _animatedCards.add(card);
      const img = card.querySelector<HTMLElement>(
        ".product-card__img:not(.product-card__img--alt)",
      );
      g.fromTo(
        card,
        { opacity: 0, y: 30 },
        {
          ..._REVEAL,
          opacity: 1,
          y: 0,
          scrollTrigger: { trigger: card, ..._ST_OPTS },
        },
      );
      if (img) {
        g.fromTo(
          img,
          { scale: 1.08 },
          {
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
            clearProps: "scale",
            scrollTrigger: { trigger: card, ..._ST_OPTS },
          },
        );
      }
    });
  });
}

// Zbierz karty jeszcze nieanimowane i ujawnij je. Idempotentne (WeakSet), więc
// bezpieczne do wielokrotnego wołania z obserwatora.
function _scanGridCards(): void {
  const fresh = _els(".listing__grid .product-card:not(.is-loading)").filter(
    (card) => !_animatedCards.has(card),
  );
  _revealCards(fresh);
}

// Karty 5+ to <LazyProductCard hydrate-on-visible>. Przy nawigacji client-side (SPA,
// bez SSR) montują się DOPIERO po wejściu w viewport / doładowaniu chunku — długo po
// jednorazowym _scanGridCards(). Bez tego zostają z CSS opacity:0 (klasa intro) i są
// niewidoczne (widać tylko 4 pierwsze, eager). MutationObserver rewiduje bezpośrednie
// dzieci siatki i animuje każdą świeżo zamontowaną kartę. Na pełnym ładowaniu (SSR)
// wszystkie karty są już w DOM przy pierwszym skanie, więc obserwator nic nie robi.
function _observeGrid(): void {
  if (_gridObserver || typeof MutationObserver === "undefined") return;
  const grid = _el(".listing__grid");
  if (!grid) return;
  _gridObserver = new MutationObserver(() => {
    if (_rafReveal) return;
    _rafReveal = requestAnimationFrame(() => {
      _rafReveal = 0;
      _scanGridCards();
    });
  });
  _gridObserver.observe(grid, { childList: true });
}

// Pełny reset siatki: nowy zestaw produktów (zmiana sortowania/filtra przez watch
// productsFetching). Kasujemy stare ScrollTriggery, czyścimy znaczniki i animujemy
// świeżo wyrenderowaną siatkę, po czym pilnujemy doładowań lazy przez _observeGrid().
function _animateGrid(): void {
  if (!_gsap || !_st) return;
  _ctx?.revert();
  _animatedCards = new WeakSet<HTMLElement>();
  _ctx = _gsap.context(() => {}, document.body);
  _scanGridCards();
  _observeGrid();
}

// .newsletter is rendered unconditionally in this SFC — guaranteed in DOM on onMounted
function _animateNewsletter(): void {
  if (!_gsap) return;
  const newsletter = _el(".newsletter");
  const newsletterItems = _els(".newsletter__head, .newsletter__form");
  if (!newsletter || !newsletterItems.length) return;
  _ctxNewsletter?.revert();
  _ctxNewsletter = _gsap.context(() => {
    _gsap!.fromTo(
      newsletterItems,
      { opacity: 0, y: 30 },
      {
        ..._REVEAL,
        opacity: 1,
        y: 0,
        stagger: 0.1,
        scrollTrigger: { trigger: newsletter, ..._ST_OPTS },
      },
    );
  }, document.body);
}

onMounted(async () => {
  if (
    typeof window === "undefined" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    // Intro-animacje TYLKO na desktopie (≥1024 px). Na mobile/tablecie (95 % ruchu)
    // koszt GSAP jest głównym składnikiem TBT: dynamiczny import biblioteki + jeden
    // ScrollTrigger na KAŻDĄ z kilkudziesięciu kart siatki, wykonywane w oknie
    // pomiaru Lighthouse (który nie scrolluje, więc i tak nic z tego nie widać).
    // Pominięcie = mniejszy TBT ORAZ karty/tytuł widoczne od pierwszego paintu
    // (patrz gate @media w <style>: opacity:0 stosujemy dopiero od 1024 px), co
    // poprawia Speed Index i FCP. Desktop (mniej krytyczny) zachowuje polish.
    !window.matchMedia("(min-width: 1024px)").matches
  ) {
    introAnimationsEnabled.value = false;
    return;
  }

  _els(".cat-hero__title, .cat-hero__cta").forEach((el) => {
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
  });

  try {
    const [{ gsap: g }, { ScrollTrigger: ST }] = await Promise.all([
      import("gsap"),
      import("gsap/ScrollTrigger"),
    ]);
    _gsap = g as _GsapApi;
    _st = ST as _StApi;
    _gsap.registerPlugin(_st);
  } catch {
    _els(".cat-hero__title, .cat-hero__cta").forEach((el) => {
      el.style.opacity = "";
      el.style.transform = "";
    });
    introAnimationsEnabled.value = false;
    return;
  }

  _animateCatHero();
  _animateNewsletter();

  _unwatchAnim = watch(
    productsFetching,
    async (fetching) => {
      if (fetching || productsLoading.value || !products.value.length) return;
      await nextTick();
      await nextTick(); // two ticks: first flushes Vue DOM update, second lets browser complete layout
      _animateGrid();
    },
    { immediate: true },
  );
});

watch(lastSegment, async () => {
  if (!_gsap) return;
  await nextTick();
  _animateCatHero();
});

onUnmounted(() => {
  _unwatchAnim?.();
  _gridObserver?.disconnect();
  _gridObserver = null;
  if (_rafReveal) {
    cancelAnimationFrame(_rafReveal);
    _rafReveal = 0;
  }
  _ctx?.revert();
  _ctxNewsletter?.revert();
  _st?.getAll().forEach((t) => t.kill());
  _gsap = null;
  _st = null;
  _ctx = null;
  _ctxNewsletter = null;
  _unwatchAnim = null;
  _animatedCards = new WeakSet<HTMLElement>();
});
</script>

<template>
  <div
    class="category-page"
    :class="{ 'category-page--intro-animations': introAnimationsEnabled }"
  >
    <CategoryBreadcrumbs :items="breadcrumbs" />

    <!-- ── Mini hero ── -->
    <!-- Wariant `--marka`: sklep katalogowy nie ma fotografii działów (klientka
         proles: „nie dajemy zdjęć na kategoriach, tylko ikony w turkusie").
         Zamiast zdjęcia modelki — płaszczyzna w kolorze marki z ikoną działu,
         ta sama co w pasku „Czego szukasz?" na stronie głównej. -->
    <section
      class="cat-hero"
      :class="{ 'cat-hero--marka': !categoryBanner.image }"
    >
      <div v-if="categoryBanner.image" class="cat-hero__media">
        <!-- Hero kategorii = najczęstszy element LCP (baner 1920×300 na samej
             górze widoku). loading=eager + fetchpriority=high ściągają go od razu
             w krytycznej ścieżce, zamiast domyślnego lazy NuxtImg.
             sizes MUSI mieć klucze breakpointów (xs..3xl), bo @nuxt/image v2
             parseSizes token bez prefiksu ("100vw") traktuje jak klucz "1px"
             (parseInt("1px")=1) → srcset s_1x300 1w, s_2x300 2w → baner 2px
             szerokości = niewidoczny. Ta sama regresja co na home (6698b52).
             BEZ prop `preload`: przy w-descriptorowym srcset NuxtImg nie emituje
             imagesrcset w preloadzie, więc preload ściągałby największy wariant
             (3400px) na mobile; fetchpriority=high + eager wystarczą (hero jest
             na górze HTML, wykrywany wcześnie).
             quality niższe niż globalne 95 — baner jest przyciemniony overlayem,
             więc mniejszy plik nie jest widoczny gołym okiem, a skraca LCP. -->
        <NuxtImg
          :src="categoryBanner.image"
          :alt="categoryBanner.alt"
          width="1920"
          height="300"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw 3xl:100vw"
          format="webp"
          :quality="65"
          loading="eager"
          fetchpriority="high"
          class="cat-hero__img"
        />
        <div class="cat-hero__overlay" />
      </div>
      <div class="cat-hero__content">
        <span v-if="!categoryBanner.image" class="cat-hero__ikona">
          <KategoriaIkona :nazwa="dzialSlug" />
        </span>
        <h1 class="cat-hero__title">
          {{ category?.name?.toUpperCase() ?? tytulZapasowy }}
        </h1>
        <NuxtLink to="/search" class="btn btn-pill-light cat-hero__cta">
          ZOBACZ PRODUKTY
        </NuxtLink>
      </div>
    </section>

    <!-- ── Listing ── -->
    <div class="listing container">
      <ProductFilters
        :listing="visibleListing"
        :is-initial-loading="primaryFiltersLoading"
        :is-updating="primaryFiltersFetching && !primaryFiltersLoading"
        mobile-modal-filters
        :can-expand-filters="canExpandFilters"
        :can-collapse-filters="canCollapseFilters"
        :is-expanding-filters="isExpandingFilters"
        :expand-filters-error="expandFiltersError"
        @expand-filters="panels.expandAllFilters()"
        @collapse-filters="panels.collapseAllFilters()"
      />

      <div class="listing__right">
        <div class="listing__toolbar">
          <p
            v-if="!productsLoading && pagination && pagination.totalItems > 0"
            class="listing__count"
          >
            Wyświetlam {{ pagination.from }}–{{ pagination.to }}
            {{ polishProductPlural(pagination.to - pagination.from + 1) }}
            z {{ pagination.totalItems.toLocaleString("pl-PL") }}
            {{ polishProductPlural(pagination.totalItems, true) }}
          </p>
          <p
            v-else-if="
              !productsLoading && pagination && pagination.totalItems === 0
            "
            class="listing__count"
          >
            Brak produktów w tej kategorii
          </p>
          <span v-else class="listing__count listing__count--placeholder" />
          <div v-if="sortOptions.length" class="listing__sort">
            <label class="listing__sort-label" for="listing-sort-select">
              Sortuj po:
            </label>
            <select
              id="listing-sort-select"
              class="listing__sort-select"
              :value="selectedSort"
              aria-label="Sortuj produkty"
              @change="
                onSortChange(($event.target as HTMLSelectElement).value)
              "
            >
              <option
                v-for="option in sortOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div
          class="listing__grid"
          :class="{ 'is-fetching': productsFetching && !productsLoading }"
        >
          <template v-if="productsLoading">
            <ProductCard
              v-for="n in 6"
              :key="`skeleton-${n}`"
              :product="{} as any"
              :loading="true"
            />
          </template>
          <template v-else>
            <!-- Pierwsze 4 karty (widoczne nad zgięciem): zwykły ProductCard,
                 hydratowany od razu → pasek "Dodaj do koszyka" z pierwszego ekranu
                 jest interaktywny natychmiast. `eager` (loading=eager +
                 fetchpriority=high) tylko dla 2 pierwszych: na mobile to 1. rząd, a
                 mniej obrazów high-priority = mniej rywalizacji o pasmo z banerem
                 hero (element LCP). Karty 3–4 mają obraz lazy, ale hydrację eager.

                 Karty 5+ (poniżej zgięcia): LazyProductCard hydrate-on-visible —
                 HTML jest SSR-owany (SEO widzi wszystkie produkty), ale hydracja
                 Vue czeka, aż karta wejdzie w viewport. Lighthouse NIE scrolluje,
                 więc kilkadziesiąt kart nigdy się nie hydratuje w oknie pomiaru →
                 znika największy składnik TBT (script eval hydracji). Realny user
                 klika kartę dopiero gdy ją widzi = już zhydratowaną. Ten sam,
                 sprawdzony mechanizm co FaqSection/NewsletterSection na home. -->
            <template v-for="(product, index) in products" :key="product.id">
              <ProductCard
                v-if="index < 4"
                :product="product"
                :eager="index < 2"
                :item-list-id="_listItemListId"
                :item-list-name="_listItemListName"
                :index="index"
              />
              <LazyVisibleProductCard
                v-else
                hydrate-on-visible
                :product="product"
                :item-list-id="_listItemListId"
                :item-list-name="_listItemListName"
                :index="index"
              />
            </template>
          </template>
        </div>
        <ProductPagination
          v-if="!productsLoading && pagination && pagination.totalPages > 1"
          :current-page="pagination.currentPage"
          :total-pages="pagination.totalPages"
          :total-items="pagination.totalItems"
          :previous-page-url="pagination.previousPageUrl"
          :next-page-url="pagination.nextPageUrl"
          :pages="pagination.pages"
        />
      </div>
    </div>

    <!-- ── Newsletter ── -->
    <NewsletterSection class="category-page__newsletter" />
  </div>
</template>

<style scoped>
.category-page {
  display: flex;
  flex-direction: column;
}

/* ── Mini hero ── */
.cat-hero {
  position: relative;
  height: 300px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding-left: calc((100vw - var(--container)) / 2);
  padding-bottom: 48px;
}

.cat-hero__media {
  position: absolute;
  inset: 0;
}

.cat-hero__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cat-hero__overlay {
  position: absolute;
  inset: 0;
  background: rgba(21, 21, 21, 0.25);
}

.cat-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}

.cat-hero__title {
  margin: 0;
  font-size: 2.25rem;
  font-weight: var(--font-weight-light);
  color: var(--color-white);
  letter-spacing: 0.6rem;
  line-height: 1;
}

.cat-hero__cta {
  font-size: 0.8125rem;
  min-height: 44px;
}

/* ── Wariant sklepu katalogowego: ikona działu zamiast fotografii ──
   Bez zdjęcia nie ma kadru do pokazania, więc baner jest niższy, a treść
   stoi w pionie pośrodku zamiast przy dolnej krawędzi.

   Podwójna klasa celowo: `@media (max-width: 960px)` niżej ustawia na
   `.cat-hero` sztywne `height: 220px`. Przy tej samej specyficzności
   wygrałoby jako późniejsze w pliku, a ikona + tytuł + przycisk nie mieszczą
   się w 220 px — `overflow: hidden` obcinał przycisk. */
.cat-hero.cat-hero--marka {
  height: auto;
  min-height: 200px;
  align-items: center;
  padding-top: 40px;
  padding-bottom: 40px;
  /* Ciemny teal z łukiem — jak ciemne posty marki na Instagramie („Marka,
     którą widać", „Nie każde logo pasuje wszędzie"). Strona główna jest
     turkusowa, kategorie ciemne: oba warianty są w grafikach klientki,
     a różnica od razu mówi, czy jesteś na tytułowej, czy w dziale. */
  background: var(--color-brand-deep, #104646);
}

/* Łuk w ciemniejszym tealu, wychodzący zza prawej krawędzi. */
.cat-hero.cat-hero--marka::before {
  content: "";
  position: absolute;
  width: min(820px, 120vw);
  aspect-ratio: 1;
  top: -90%;
  right: max(-280px, -30vw);
  border-radius: 50%;
  background: var(--color-brand-deeper, #06393c);
  pointer-events: none;
}

/* Białe koło pod ikoną oddziela znak od tła tak samo jak kafelek w pasku
   działów — na ciemnym tealu tym bardziej. */
.cat-hero__ikona {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  padding: 14px;
  color: var(--color-accent, #64bfaa);
  background: #fff;
  border-radius: 50%;
}

/* Tytuł jak nagłówki na grafikach: pogrubione wersaliki z białą kreską.
   Biały na ciemnym tealu ma 10.6:1. Rozstrzelenie 0.6rem z wariantu
   fotograficznego zostaje lavmagowi — przy pogrubieniu rozsypywało słowo. */
.cat-hero--marka .cat-hero__title {
  color: #fff;
  font-weight: var(--font-weight-bold, 700);
  letter-spacing: 0.06em;
}

.cat-hero--marka .cat-hero__title::after {
  content: "";
  display: block;
  width: 48px;
  height: 3px;
  margin-top: 16px;
  border-radius: 2px;
  background: var(--color-accent, #64bfaa);
}

/* Przycisk w turkusie z napisem w ciemnym tealu: 4.8:1. Biały guzik
   (`btn-pill-light`) też miałby kontrast, ale turkus trzyma się palety marki. */
.cat-hero--marka .cat-hero__cta {
  background: var(--color-accent, #64bfaa);
  border-color: var(--color-accent, #64bfaa);
  color: var(--color-brand-deep, #104646);
  font-weight: var(--font-weight-bold, 700);
}

@media (hover: hover) {
  .cat-hero--marka .cat-hero__cta:hover {
    background: var(--color-accent-light, #7cd4be);
    border-color: var(--color-accent-light, #7cd4be);
    color: var(--color-brand-deep, #104646);
  }
}

/* ── Listing layout ── */
.listing {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  padding-top: 40px;
  padding-bottom: 40px;
  align-items: start;
}

.listing__right {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.listing__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 36px;
}

.listing__sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.listing__sort-label {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  white-space: nowrap;
}

.listing__sort-select {
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--color-text);
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 6px 28px 6px 10px;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='%23555' stroke-width='1.5'><path d='M1 1l4 4 4-4'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  min-height: 36px;
}

.listing__sort-select:focus {
  border-color: var(--color-primary);
}

.listing__count {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1.4;
}

.listing__count--placeholder {
  visibility: hidden;
}

.listing__grid {
  display: grid;
  /* Domyślnie 2 kolumny (do 1599px); 3 kolumny dopiero od 1600px (niżej). */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  transition:
    filter 0.2s ease,
    opacity 0.2s ease;
}

/* Ultra-wide: 3 produkty w rzędzie od 1600px. */
@media (min-width: 1600px) {
  .listing__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* Katalog (proles): packshoty gadżetów, nie zdjęcia odzieży — 2 kafle w rzędzie
   dawały przy 1536 px (1920 px przy skali 125% w Windows klientki) zdjęcia
   506 px (spotkanie 30.10.2026). Od 03.11.2026 na desktopie (≥1200 px) zawsze
   4 kafle — wcześniej 3, a 4 dopiero od 1800 px, więc każdy listing wyglądał
   inaczej zależnie od ekranu. To samo w nowosci.vue i search.vue. */
@media (min-width: 1200px) {
  [data-brand="proles"] .listing__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.listing__grid :deep(.product-card) {
  min-width: 0;
  overflow: hidden;
}

.listing__grid.is-fetching {
  opacity: 0.86;
  filter: saturate(0.92);
  pointer-events: none;
}

/* Stan początkowy intro-animacji (opacity:0) TYLKO na desktopie (≥1024 px) —
   spójnie ze strażnikiem w onMounted, który poza tym progiem w ogóle nie ładuje
   GSAP. Bez tego gate'u SSR renderuje klasę intro na mobile → karty i tytuł byłyby
   niewidoczne (opacity:0) w oknie pomiaru Lighthouse aż do (pominiętej) animacji,
   psując FCP/SI. Poniżej 1024 px treść jest widoczna od pierwszego paintu. */
@media (min-width: 1024px) and (not (prefers-reduced-motion: reduce)) {
  .category-page--intro-animations .cat-hero__title,
  .category-page--intro-animations .cat-hero__cta {
    opacity: 0;
    transform: translateY(20px);
  }

  .category-page--intro-animations .listing__grid .product-card:not(.is-loading) {
    opacity: 0;
    transform: translateY(30px);
  }

  .category-page--intro-animations :deep(.newsletter__head),
  .category-page--intro-animations :deep(.newsletter__form) {
    opacity: 0;
    transform: translateY(30px);
  }
}

/* Mobile/tablet (<1024 px, 95 % ruchu): karty poza ekranem pomijają layout/paint.
   Przeglądarka renderuje tylko te w viewporcie; reszta ma zarezerwowaną wysokość
   z contain-intrinsic-size (obraz aspect-ratio 0.75 + info strip ≈ 340 px na
   szerokości ~190 px), więc pasek scrolla i pozycje są stabilne, a `auto`
   zapamiętuje realny rozmiar po pierwszym wyrenderowaniu. Korekty przy scrollu mają
   hadRecentInput → nie liczą się do CLS. Desktop (GSAP mierzy pozycje ScrollTriggerem)
   celowo pominięty, by contain nie zaburzał pomiarów triggerów. */
@media (max-width: 1023px) {
  .listing__grid :deep(.product-card:not(.is-loading)) {
    content-visibility: auto;
    contain-intrinsic-size: auto 340px;
  }
}

.category-page__newsletter {
  margin-top: 40px;
}

/* ── Responsive ── */
@media (max-width: 1200px) {
  .listing {
    grid-template-columns: 240px 1fr;
    gap: 24px;
  }
}

@media (max-width: 960px) {
  .cat-hero {
    height: 220px;
    padding-left: 24px;
    padding-bottom: 32px;
  }
  .cat-hero.cat-hero--marka {
    padding-top: 28px;
    padding-bottom: 28px;
    min-height: 0;
  }
  .cat-hero__ikona {
    width: 52px;
    height: 52px;
    padding: 12px;
  }
  .cat-hero__title {
    font-size: 1.5rem;
    letter-spacing: 0.3rem;
  }
  .listing {
    grid-template-columns: 1fr;
  }
}
</style>
